import * as React from 'react';
import type { MediaUnitFilters } from '@ooh/contracts';

/**
 * Manages selection state for bulk-edit on the inventory page.
 *
 * - selectedIds: Set of individual row IDs currently selected, kept across
 *   search/filter/sort changes (row-selection spec) — only an offerContextKey
 *   change or an explicit clear/successful bulk action resets it.
 * - selectAllFilter: when non-null, the user clicked "Select all N matching"
 *   and the request should use filter-based selection, not IDs. That mode is
 *   a saved search: it still ends whenever filterKey changes (bulk-edit-media
 *   spec, unmodified by row-selection).
 * - Persists to sessionStorage (keyed by offerContextKey) so the selection
 *   survives a full unmount/remount of this hook — e.g. navigating from the
 *   catalog into a unit's details and back, which unmounts the
 *   `/media-units` route component and would otherwise silently drop the
 *   selection (CU-86caypxam). A stored selection is only restored when the
 *   current offerContextKey matches the one it was saved under, so a
 *   selection made in one offer's context never reaches another offer's (or
 *   the no-context catalog).
 */

const STORAGE_KEY = 'ooh:media-units:bulk-selection';

interface StoredSelection {
  offerContextKey: unknown;
  selectedIds: number[];
  selectAllFilter: MediaUnitFilters | null;
}

function readStoredSelection(offerContextKey: unknown): {
  ids: Set<number>;
  filter: MediaUnitFilters | null;
} {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ids: new Set(), filter: null };
    const parsed = JSON.parse(raw) as StoredSelection;
    if (parsed.offerContextKey !== offerContextKey) return { ids: new Set(), filter: null };
    return { ids: new Set(parsed.selectedIds), filter: parsed.selectAllFilter };
  } catch (error) {
    // Corrupt/incompatible stored payload or sessionStorage unavailable (private
    // mode, quota) — fail open to an empty selection but surface it, so a
    // systemic parse failure (e.g. a future StoredSelection shape change) is
    // visible in logs instead of indistinguishable from the expected cases.
    console.warn('useMediaBulkSelection: failed to read stored selection', error);
    return { ids: new Set(), filter: null };
  }
}

function writeStoredSelection(
  offerContextKey: unknown,
  selectedIds: Set<number>,
  selectAllFilter: MediaUnitFilters | null,
): void {
  try {
    const payload: StoredSelection = {
      offerContextKey,
      selectedIds: [...selectedIds],
      selectAllFilter,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (error) {
    // sessionStorage may be unavailable (private mode, quota) — selection just
    // won't survive a remount; the in-memory state still works for this
    // session. Surfaced via console.warn so this isn't silently invisible.
    console.warn('useMediaBulkSelection: failed to persist selection', error);
  }
}

export interface UseMediaBulkSelectionOptions {
  /** An opaque value that represents the current filter+sort+search state.
   * When it changes, "select all N matching" mode ends (that mode is a saved
   * search) — the id-based selection itself is unaffected. */
  filterKey: unknown;
  /** Identifies which offer context (or none, e.g. `null`) the catalog is open
   * in. When it changes, the whole selection (ids and select-all-filter) is
   * reset, so a selection made for one offer never reaches another. */
  offerContextKey: unknown;
}

export interface UseMediaBulkSelectionResult {
  selectedIds: Set<number>;
  selectAllFilter: MediaUnitFilters | null;
  toggleId: (id: number) => void;
  toggleAll: (ids: number[]) => void;
  clearSelection: () => void;
  activateSelectAllFilter: (filter: MediaUnitFilters) => void;
  isAllSelected: (currentPageIds: number[]) => boolean;
  someSelected: boolean;
}

// eslint-disable-next-line max-lines-per-function -- hook manages cohesive selection state; extraction would obscure the logic
export function useMediaBulkSelection(
  options: UseMediaBulkSelectionOptions,
): UseMediaBulkSelectionResult {
  const { filterKey, offerContextKey } = options;

  const [selectedIds, setSelectedIds] = React.useState<Set<number>>(
    () => readStoredSelection(offerContextKey).ids,
  );
  const [selectAllFilter, setSelectAllFilter] = React.useState<MediaUnitFilters | null>(
    () => readStoredSelection(offerContextKey).filter,
  );

  // Reset the whole selection when the offer context changes — a selection made
  // for one offer must never reach another (row-selection spec). Derived during
  // render (not an effect) per this app's "compute values during render instead
  // of syncing state through useEffect" rule: comparing against the previous
  // render's key and adjusting state directly is React's documented pattern for
  // resetting state in response to a changed identity.
  const [prevOfferContextKey, setPrevOfferContextKey] = React.useState(offerContextKey);
  const [prevFilterKey, setPrevFilterKey] = React.useState(filterKey);
  if (prevOfferContextKey !== offerContextKey) {
    setPrevOfferContextKey(offerContextKey);
    setPrevFilterKey(filterKey);
    const restored = readStoredSelection(offerContextKey);
    setSelectedIds(restored.ids);
    setSelectAllFilter(restored.filter);
  } else if (prevFilterKey !== filterKey) {
    // "Select all N matching" is a saved search: it ends on any filter/sort/
    // search change, unlike the id-based selection, which persists through it.
    setPrevFilterKey(filterKey);
    if (selectAllFilter != null) {
      setSelectAllFilter(null);
      writeStoredSelection(offerContextKey, selectedIds, null);
    }
  }

  const toggleId = React.useCallback(
    (id: number) => {
      setSelectAllFilter(null); // ID toggle exits filter-based mode
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        writeStoredSelection(offerContextKey, next, null);
        return next;
      });
    },
    [offerContextKey],
  );

  const toggleAll = React.useCallback(
    (ids: number[]) => {
      setSelectAllFilter(null);
      setSelectedIds((prev) => {
        const allSelected = ids.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allSelected) {
          for (const id of ids) next.delete(id);
        } else {
          for (const id of ids) next.add(id);
        }
        writeStoredSelection(offerContextKey, next, null);
        return next;
      });
    },
    [offerContextKey],
  );

  const clearSelection = React.useCallback(() => {
    setSelectedIds(new Set());
    setSelectAllFilter(null);
    writeStoredSelection(offerContextKey, new Set(), null);
  }, [offerContextKey]);

  const activateSelectAllFilter = React.useCallback(
    (filter: MediaUnitFilters) => {
      setSelectAllFilter(filter);
      setSelectedIds(new Set());
      writeStoredSelection(offerContextKey, new Set(), filter);
    },
    [offerContextKey],
  );

  const isAllSelected = React.useCallback(
    (currentPageIds: number[]): boolean => {
      if (currentPageIds.length === 0) return false;
      return currentPageIds.every((id) => selectedIds.has(id));
    },
    [selectedIds],
  );

  const someSelected = selectedIds.size > 0;

  return {
    selectedIds,
    selectAllFilter,
    toggleId,
    toggleAll,
    clearSelection,
    activateSelectAllFilter,
    isAllSelected,
    someSelected,
  };
}
